import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  inject,
  ViewChild,
  TemplateRef,
  Input,
  OnDestroy,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PaginatorModule } from 'primeng/paginator';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { Toast } from 'primeng/toast';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { AddTask } from './add-task/add-task';
import { TaskSchedulerService } from '@services/task-scheduler.service';
import { TaskResultDisplayItem } from '@models/task-scheduler.models';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { DataTableComponent, TableColumn } from '@components/data-table/data-table.component';
import {
  SystemBackgroundTask,
  SystemBackgroundTaskService,
  SystemBackgroundTaskResultService,
  LocalizationString,
} from '@6grain/masofa-monolith-api-client/common';
import { FieldFilter } from '@6grain/masofa-monolith-api-client/dictionaries';
import { ActivatedRoute } from '@angular/router';
import { CustomMessageService } from '@app/interceptors/custom-message';
import { LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CustomPaginatorComponent } from '../../Ui-Kit/custom-paginator/custom-paginator.component';

@Component({
  selector: 'app-task-scheduler-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TabsModule,
    TableModule,
    TagModule,
    PaginatorModule,
    ToggleSwitchModule,
    FormsModule,
    CheckboxModule,
    Toast,
    BackButton,
    Loading,
    IconField,
    InputIcon,
    DataTableComponent,
    CustomPaginatorComponent,
  ],
  providers: [
    DialogService,
    DatePipe,
    {
      provide: MessageService,
      useClass: CustomMessageService,
    },
  ],
  templateUrl: './task-scheduler-page.html',
  styleUrl: './task-scheduler-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskSchedulerPageComponent implements OnInit, OnDestroy {
  private systemBackgroundTaskService = inject(SystemBackgroundTaskService);
  private systemBackgroundTaskResultService = inject(SystemBackgroundTaskResultService);
  private translateService = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private langChangeSubscription?: Subscription;

  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
  @ViewChild('errorsTemplate', { static: true }) errorsTemplate!: TemplateRef<any>;

  @Input() filterParams: FieldFilter | null = null;
  currentSystemTask: SystemBackgroundTask | null = null;

  lang = this.translateService.currentLang as keyof LocalizationString;

  loading: boolean = true;
  loadingSystemBackgroundTask: boolean = true;
  loadingHistory: boolean = false;
  activeTab: string = '0';
  searchQuery: string = '';
  isSmallScreen: boolean = false;

  pageSize: number = 10;
  totalRecords: number = 0;

  selectedTask: SystemBackgroundTask | null = null;
  selectAll: boolean = false;
  selectedExecutionId: string | null = null;

  taskSchedulerService = inject(TaskSchedulerService);
  private cdr = inject(ChangeDetectorRef);
  private ref: DynamicDialogRef | undefined;

  rowsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  allTasks: SystemBackgroundTask[] = [];
  scheduledTasks: SystemBackgroundTask[] = [];
  filteredTasks: SystemBackgroundTask[] = [];

  executionHistory: TaskResultDisplayItem[] = [];
  paginatedExecutionHistory: TaskResultDisplayItem[] = [];
  historyFirst: number = 0;
  historyRows: number = 10;

  executionHistoryFiltered: any[] = [];
  tableColumns: TableColumn[] = [];

  protected filterKeywords: string[] = [];
  parentRoute: string = '/admin';

  constructor(
    private dialogService: DialogService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const routeData = this.route.snapshot.data;
    if (routeData['parentRoute']) {
      this.parentRoute = routeData['parentRoute'];
    }
    if (routeData['filterKeywords'] && Array.isArray(routeData['filterKeywords'])) {
      this.filterKeywords = routeData['filterKeywords'];
    }

    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['module']) {
      this.filterParams = {
        filterField: 'module',
        filterValue: queryParams['module'],
        filterOperator: 0,
      };
    }

    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.lang = event.lang as keyof LocalizationString;
      this.cdr.markForCheck();
    });

    this.tableColumns = [
      {
        field: 'createdAt',
        header: 'Created At',
        type: 'date',
        defaultSorted: true,
      },
      { field: 'resultType', header: 'Status', type: 'custom', customTemplate: this.statusTemplate },
      { field: 'errorMessages', header: 'Errors', type: 'custom', customTemplate: this.errorsTemplate },
    ];

    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  private loadData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.systemBackgroundTaskService.commonSystemBackgroundTaskGetByQueryPost({}).subscribe({
      next: (tasks) => {
        this.allTasks = tasks;

        if (this.filterKeywords.length > 0) {
          this.scheduledTasks = tasks.filter((task) => {
            const typeName = task.executeTypeName || '';
            const isMatch = this.filterKeywords.some((keyword) => typeName.includes(keyword));
            return isMatch;
          });
        } else {
          this.scheduledTasks = tasks;
        }
        this.filteredTasks = this.scheduledTasks;
        this.totalRecords = this.scheduledTasks.length;
        this.loading = false;
        this.cdr.markForCheck();
        this.loadHistory();
      },
      error: (err) => {
        console.error('Error loading tasks:', err);
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadHistory(): void {
    this.loadingHistory = true;
    this.cdr.markForCheck();

    this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetByQueryPost({}).subscribe({
      next: (results) => {
        const mappedHistory = results.map((r) => {
          let task = this.allTasks.find((t) => t.id === r.systemBackgroundTaskId);

          if (!task && r.taskResultJsonTypeName) {
            const shortResultName = r.taskResultJsonTypeName.split('.').pop()?.replace('Result', '') || '';

            task = this.allTasks.find((t) => {
              if (!t.executeTypeName) return false;
              const shortTaskName = t.executeTypeName.split('.').pop() || '';
              return shortTaskName === shortResultName;
            });
          }

          let isMatch = false;

          if (this.filterKeywords.length > 0) {
            if (task && task.executeTypeName) {
              isMatch = this.filterKeywords.some((keyword) => task!.executeTypeName!.includes(keyword));
            } else {
              const typeName = r.taskResultJsonTypeName?.toLowerCase() || '';
              isMatch = this.filterKeywords.some((keyword) => typeName.includes(keyword.toLowerCase()));
            }
          } else {
            isMatch = true;
          }

          if (!isMatch) {
            return null;
          }

          const fallbackName = r.taskResultJsonTypeName?.split('.').pop()?.replace('Result', '') || 'Unknown Task';

          return {
            id: r.id || '',
            taskName: task?.names || {
              'en-US': fallbackName,
              'ru-RU': fallbackName,
              'uz-Latn-UZ': fallbackName,
            },
            executeTypeName: task?.executeTypeName || r.taskResultJsonTypeName,
            resultType: r.resultType,
            createdAt: r.createAt,
            status: r.resultType === 0 ? 'success' : 'failed',
          } as TaskResultDisplayItem;
        });

        this.executionHistory = mappedHistory
          .filter((item): item is TaskResultDisplayItem => item !== null)
          .sort((a, b) => {
            const dateA = new Date(a.createdAt ?? 0).getTime();
            const dateB = new Date(b.createdAt ?? 0).getTime();
            return dateB - dateA;
          });

        this.historyFirst = 0;
        this.updateHistoryView();

        this.loadingHistory = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading task results:', err);
        this.loadingHistory = false;
        this.cdr.markForCheck();
      },
    });
  }

  private updateHistoryView(): void {
    const end = this.historyFirst + this.historyRows;
    this.paginatedExecutionHistory = this.executionHistory.slice(this.historyFirst, end);
    this.cdr.markForCheck();
  }

  onHistoryPageChange(event: any): void {
    this.historyFirst = event.first;
    this.historyRows = event.rows;
    this.updateHistoryView();
  }

  onHistoryPageSizeChange(newSize: number): void {
    this.historyRows = newSize;
    this.historyFirst = 0;
    this.updateHistoryView();
  }

  addNewTask(): void {
    this.ref = this.dialogService.open(AddTask, {
      header: this.translate.instant('Add a new task'),
      width: '1000px',
      height: 'auto',
      contentStyle: { overflow: 'visible' },
      baseZIndex: 10000,
      maximizable: false,
      modal: true,
      closable: true,
      styleClass: 'add-task-dialog',
      data: {
        filterKeywords: this.filterKeywords,
      },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result?.success) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Task created successfully'),
        });
        this.loadData();
      }
    });
  }

  editTask(task: SystemBackgroundTask): void {
    this.ref = this.dialogService.open(AddTask, {
      header: this.translate.instant('Edit task'),
      width: '1000px',
      height: 'auto',
      contentStyle: { overflow: 'visible' },
      baseZIndex: 10000,
      maximizable: false,
      modal: true,
      closable: true,
      styleClass: 'add-task-dialog',
      data: {
        task,
        possibleExecuteType: this.selectedTask,
        filterKeywords: this.filterKeywords,
      },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result?.success) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Task updated successfully'),
        });
      }
    });

    this.loadData();
  }

  deleteTask(task: SystemBackgroundTask): void {
    this.systemBackgroundTaskService.commonSystemBackgroundTaskDeleteIdDelete(task.id!).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Task deleted successfully'),
        });
      },
      error: (err) => {
        console.error('Error deleting task:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error'),
          detail: this.translate.instant('Failed to delete task'),
        });
      },
    });

    this.loadData();
  }

  selectExecution(execution: TaskResultDisplayItem): void {
    this.selectedExecutionId = this.selectedExecutionId === execution.id ? null : execution.id;
  }

  selectTask(task: SystemBackgroundTask): void {
    this.selectedTask = task;

    this.currentSystemTask = null;

    if (task.executeTypeName) {
      this.loadingSystemBackgroundTask = true;
      this.cdr.markForCheck();

      this.systemBackgroundTaskResultService
        .commonSystemBackgroundTaskResultGetByQueryPost({
          filters: [
            {
              filterField: 'systemBackgroundTaskId',
              filterValue: task.id,
              filterOperator: 0,
            },
          ],
        })
        .subscribe({
          next: (results) => {
            this.executionHistoryFiltered = results.map((r) => {
              let errorMessages = '';
              let hasErrors = false;
              const resultType = r.resultType ?? 0;

              try {
                if (r.taskResultJson) {
                  const resultJson =
                    typeof r.taskResultJson === 'string' ? JSON.parse(r.taskResultJson) : r.taskResultJson;
                  if (
                    resultJson &&
                    resultJson.Errors &&
                    Array.isArray(resultJson.Errors) &&
                    resultJson.Errors.length > 0
                  ) {
                    hasErrors = true;
                    errorMessages = resultJson.Errors.join(', ');
                  }
                }
              } catch (e) {
                console.error('Error parsing taskResultJson', e);
              }

              if (resultType !== 0) {
                hasErrors = true;
                if (!errorMessages) {
                  errorMessages = 'Failed (No error details)';
                }
              }

              return {
                id: r.id || '',
                createdAt: r.createAt,
                createdAtFormatted: this.datePipe.transform(r.createAt, 'yyyy-MM-dd'),
                hasErrors: hasErrors,
                errorMessages: errorMessages,
                resultType: resultType,
                status: resultType === 0 ? 'success' : 'failed',
              };
            });
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error loading task results:', err);
          },
        });

      this.systemBackgroundTaskService
        .commonSystemBackgroundTaskGetByQueryPost({
          filters: [
            {
              filterField: 'executeTypeName',
              filterValue: task.executeTypeName,
              filterOperator: 0,
            },
          ],
        })
        .subscribe({
          next: (tasks) => {
            if (tasks && tasks.length > 0) {
              this.currentSystemTask = tasks[0];
            }
            this.loadingSystemBackgroundTask = false;
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error loading SystemBackgroundTask:', err);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('Error'),
              detail: this.translate.instant('Failed to load task details'),
            });
            this.loadingSystemBackgroundTask = false;
            this.cdr.markForCheck();
          },
        });
    }
  }

  getActiveTasksCount(): number {
    return this.scheduledTasks.filter((task) => task.isActive).length;
  }

  updateTaskStatus(task: SystemBackgroundTask, isActive: boolean): void {
    let systemBackgroundTask: SystemBackgroundTask = task;
    systemBackgroundTask.isActive = isActive;

    this.systemBackgroundTaskService.commonSystemBackgroundTaskUpdatePut(systemBackgroundTask).subscribe({
      next: (response) => {
        const status = isActive ? 'activated' : 'deactivated';
        this.messageService.add({
          severity: 'success',
          summary: 'Status Updated',
          detail: task.names?.['en-US'] ?? '',
        });
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translate.instant('Failed to update task status'),
        });
        task.isActive = !isActive;
        this.cdr.markForCheck();
      },
    });
  }

  onSearch(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredTasks = this.scheduledTasks;
      return;
    }
    const query = this.searchQuery.toLowerCase();
    this.filteredTasks = this.scheduledTasks.filter((task) => {
      const name = task.names?.[this.lang]?.toLowerCase() || '';
      return name.includes(query);
    });
  }
}
